<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { cn } from '$lib/utils/cn';

    import { flatten } from 'lottie-colorify';
    import { onMount } from 'svelte';

    import IconContent from './IconContent.svelte';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import { getFixedAnimateLottie } from '../_utils/getFixedAnimateLottie';
    import Box from '../_wrappers/Box.svelte';

    import type { Box as BoxType, LeftCenterAlign } from '$lib/types/Components/SharedTypes';
    import type { Size } from '$lib/types/Components/SharedTypes';
    import type { TransitionEffect } from '$lib/types/Theme';

    export let id = '';
    export let box: BoxType;
    export let size: Size = 'small';
    export let align: LeftCenterAlign = 'center';
    export let icon = 'adb';
    export let color = '#0A0A0A';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let svg = '';
    export let iconName = '';
    export let platform = '';
    export let isInMediaAnswer = false;
    export let isAnimated = false;
    export let jsonUrl: string | null = null;
    export let layout = DEFAULT_LAYOUT;
    export let linkingEnabled = false;
    export let onClick: () => void = () => {};
    export let onMouseEnter: (event: MouseEvent | TouchEvent) => void = () => {};

    const COLORED_PLATFORMS = [
        'doodle',
        'dusk',
        'cool',
        'color',
        'cotton',
        'clouds',
        'bubbles',
        'fluent',
        'office30',
        'office40',
        'clr-gls',
        'ultraviolet',
        'plasticine',
    ];

    const { isInColumn, isInList } = layout;
    const isPreColoredIcon = COLORED_PLATFORMS.includes(platform || '');

    let wrapperClasses: string;
    let lottieJson: string | null = null;

    onMount(async () => {
        if (isAnimated && jsonUrl) {
            await import('@lottiefiles/lottie-player');

            try {
                const response = await fetch(jsonUrl);

                const json = await response.json();

                lottieJson = isPreColoredIcon ? json : flatten(color, getFixedAnimateLottie(json));
            } catch (err) {
                console.warn('Error fetching Lottie JSON', {
                    jsonUrl,
                    error: err,
                });
            }
        }
    });

    const decodeSVG = () => {
        const fallback = `<img src='https://img.icons8.com/${color.replace(
            '#',
            '',
        )}/${platform}/200/${iconName}' alt='${iconName}' width='100%' />`;

        if (svg) {
            try {
                return decodeURI(svg);
            } catch {
                return fallback;
            }
        } else {
            return fallback;
        }
    };

    // Keeping existing classes for backwards compatibility
    if (isInList) {
        wrapperClasses = cn('fun-w-full');
    } else {
        wrapperClasses = cn({
            'fun-w-1/4 fun-max-w-[32px]': size === 'xSmall',
            'fun-w-1/3 fun-max-w-[48px]': size === 'small',
            'fun-w-1/2 fun-max-w-[77px]': size === 'medium' || size === 'regular',
            'fun-w-3/4 fun-max-w-[155px]': size === 'large',
            'fun-w-full fun-max-w-[278px]': size === 'xLarge' || size === 'title2',
            'fun-w-16': size === 'answer',
        });
    }
</script>

<Box
    {id}
    {...getBoxAttributes({ box, isInColumn, isInMediaAnswer })}
    {transitionDelay}
    {transitionEffect}
    {align}
    narrow
>
    {#if linkingEnabled}
        <button
            type="button"
            class={cn(
                'wrapper fun-block fun-max-w-full fun-cursor-pointer fun-border-0 fun-p-0 fun-transition-transform',
                wrapperClasses,
                {
                    'fun-mx-auto': align === 'center',
                },
            )}
            style="--fill-color: {color}"
            on:click={onClick}
            on:mouseenter={onMouseEnter}
            on:touchstart={onMouseEnter}
            aria-label={iconName || 'Icon'}
        >
            <IconContent
                {isAnimated}
                {lottieJson}
                {svg}
                {platform}
                {iconName}
                {icon}
                {color}
                {decodeSVG}
            />
        </button>
    {:else}
        <div
            class={cn('wrapper', wrapperClasses, {
                'fun-mx-auto': align === 'center',
            })}
            style="--fill-color: {color}"
        >
            <IconContent
                {isAnimated}
                {lottieJson}
                {svg}
                {platform}
                {iconName}
                {icon}
                {color}
                {decodeSVG}
            />
        </div>
    {/if}
</Box>

<style lang="scss">
    .wrapper > :global(svg) {
        fill: var(--fill-color);
        width: 100%;
        height: 100%;
    }
</style>
